import { useEffect, useState, useCallback } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, BarChart2, ListChecks, Zap, RefreshCw,
} from 'lucide-react';
import { pulseApi } from '../../api/client';
import { useAppStore } from '../../store/useAppStore';
import { OpportunityCard } from '../../components/Pulse/OpportunityCard';
import { SkeletonOpportunityRow } from '../../components/common/Skeleton';
import { MarketRadarTicker } from '../../components/Pulse/MarketRadarTicker';
import { MarketPressureMap } from '../../components/Pulse/MarketPressureMap';
import { DecisionQueue } from '../../components/Pulse/DecisionQueue';
import { formatCurrency, formatPct } from '../../utils/format';
import type { SportRecommendation } from '../../types';

// ── Timing sort order ─────────────────────────────────────────────────────────
const TIMING_ORDER: Record<string, number> = { BET_NOW: 0, WAIT: 1, MONITOR: 2, AVOID: 3 };

// ── Sport filter chip ─────────────────────────────────────────────────────────
type SportFilter = 'all' | 'mlb' | 'wnba' | 'nba';

function FilterChip({ label, active, count, onClick }: { label: string; active: boolean; count?: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide transition-all duration-100',
        active
          ? 'bg-brand/20 text-brand border border-brand/30'
          : 'text-gray-500 hover:text-gray-200 hover:bg-surface-2 border border-transparent',
      )}
    >
      {label}
      {count != null && count > 0 && (
        <span className={clsx('tabular-nums', active ? 'text-brand' : 'text-gray-600')}>{count}</span>
      )}
    </button>
  );
}

// ── PulseCommandWorkspace ─────────────────────────────────────────────────────

export function PulseCommandWorkspace() {
  const {
    bankrollSettings, exposure, bets,
    signals,
    connectionStatus,
  } = useAppStore();

  const [picks, setPicks] = useState<SportRecommendation[]>([]);
  const [picksLoading, setPicksLoading] = useState(true);
  const [sportFilter, setSportFilter] = useState<SportFilter>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [overview, setOverview] = useState<{ todayPicks: number; activeBets: number; winRateL30: number | null; roiL30: number | null } | null>(null);

  const loadPicks = useCallback(async () => {
    setPicksLoading(true);
    try {
      const data = await pulseApi.getPicks({ limit: 50 });
      setPicks(data.filter((r) => r.decisionStatus !== 'INVALID'));
    } catch {
      // non-critical
    } finally {
      setPicksLoading(false);
    }
  }, []);

  // Load data
  useEffect(() => {
    void loadPicks();
    pulseApi.getOverview().then((data) => setOverview(data)).catch(() => {});
  }, [loadPicks]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadPicks();
      await pulseApi.getOverview().then((data) => setOverview(data)).catch(() => {});
    } finally {
      setIsRefreshing(false);
    }
  }, [loadPicks]);

  // ── Filtered + sorted opportunities ─────────────────────────────────────────
  const filtered = picks
    .filter((r) => {
      if (sportFilter !== 'all' && r.leagueId !== sportFilter) return false;
      return r.decisionStatus !== 'NO_BET';
    })
    .sort((a, b) => {
      const ta = TIMING_ORDER[a.timing] ?? 99;
      const tb = TIMING_ORDER[b.timing] ?? 99;
      if (ta !== tb) return ta - tb;
      return (b.edge ?? 0) - (a.edge ?? 0);
    });

  const betNowCount = filtered.filter((r) => r.timing === 'BET_NOW').length;
  const qualifiedCount = filtered.filter((r) =>
    r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET',
  ).length;

  // Per-sport recommendation counts
  const mlbCount = picks.filter((r) => r.leagueId === 'mlb' && r.decisionStatus !== 'NO_BET').length;
  const wnbaCount = picks.filter((r) => r.leagueId === 'wnba' && r.decisionStatus !== 'NO_BET').length;
  const nbaCount = picks.filter((r) => r.leagueId === 'nba' && r.decisionStatus !== 'NO_BET').length;

  // ── Bankroll stats ────────────────────────────────────────────────────────────
  const openBets = bets.filter((b) => b.status === 'placed' || b.status === 'live').length;
  const bankroll = bankrollSettings?.currentBankroll;
  const totalExp = exposure?.totalOpen;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* ── Workspace header ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-surface-4 bg-surface-1/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple/10 border border-purple/20">
            <Activity className="w-3.5 h-3.5 text-purple-bright" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-100 leading-tight">Pulse Command</p>
            <p className="text-[9px] text-gray-500 uppercase tracking-widest leading-tight">Intelligence OS</p>
          </div>
        </div>

        {/* Sport filters */}
        <div className="flex items-center gap-1 ml-4">
          <FilterChip label="All" active={sportFilter === 'all'} count={picks.filter((r) => r.decisionStatus !== 'NO_BET' && r.decisionStatus !== 'INVALID').length} onClick={() => setSportFilter('all')} />
          <FilterChip label="MLB" active={sportFilter === 'mlb'} count={mlbCount} onClick={() => setSportFilter('mlb')} />
          <FilterChip label="WNBA" active={sportFilter === 'wnba'} count={wnbaCount} onClick={() => setSportFilter('wnba')} />
          <FilterChip label="NBA" active={sportFilter === 'nba'} count={nbaCount} onClick={() => setSportFilter('nba')} />
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-2 ml-auto">
          {betNowCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-gold/10 border border-gold/25 text-[10px] font-bold text-gold-bright">
              <span className="w-1 h-1 rounded-full bg-gold animate-pulse-slow" />
              {betNowCount} BET NOW
            </span>
          )}
          {qualifiedCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-brand/10 border border-brand/25 text-[10px] font-semibold text-brand">
              {qualifiedCount} Qualified
            </span>
          )}
          <span className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold',
            connectionStatus === 'connected' ? 'text-win' : connectionStatus === 'connecting' ? 'text-warn' : 'text-gray-500',
          )}>
            <span className={clsx(
              'w-1.5 h-1.5 rounded-full',
              connectionStatus === 'connected' ? 'bg-win animate-pulse-slow' : connectionStatus === 'connecting' ? 'bg-warn animate-pulse' : 'bg-gray-600',
            )} />
            {connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Syncing' : 'Offline'}
          </span>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || picksLoading}
            className="flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors disabled:opacity-40"
            title="Refresh recommendations"
          >
            <RefreshCw className={clsx('w-3.5 h-3.5', (isRefreshing || picksLoading) && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* ── KPI strip ───────────────────────────────────────────────────────── */}
      <div className="flex items-stretch border-b border-surface-4 flex-shrink-0 bg-surface-1/30">
        {[
          { label: 'Today\'s Picks', value: overview?.todayPicks != null ? String(overview.todayPicks) : String(qualifiedCount), color: 'text-brand' },
          { label: 'Active Bets', value: overview?.activeBets != null ? String(overview.activeBets) : String(openBets), color: 'text-info' },
          { label: 'Win Rate L30', value: overview?.winRateL30 != null ? formatPct(overview.winRateL30) : '—', color: overview?.winRateL30 != null && overview.winRateL30 >= 0.55 ? 'text-win' : 'text-gray-400' },
          { label: 'ROI L30', value: overview?.roiL30 != null ? `${overview.roiL30 >= 0 ? '+' : ''}${(overview.roiL30 * 100).toFixed(1)}%` : '—', color: overview?.roiL30 != null ? (overview.roiL30 >= 0 ? 'text-win' : 'text-loss') : 'text-gray-400' },
          { label: 'Exposure', value: totalExp != null ? formatCurrency(totalExp) : '—', color: totalExp && bankroll && totalExp / bankroll > 0.3 ? 'text-warn' : 'text-gray-300' },
          { label: 'Bankroll', value: bankroll != null ? formatCurrency(bankroll) : '—', color: 'text-gray-200' },
        ].map(({ label, value, color }) => (
          <div key={label} className="flex-1 flex flex-col gap-1 px-3 py-2 border-r border-surface-4 last:border-r-0 min-w-0">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600 leading-none select-none">{label}</span>
            <span className={clsx('text-base font-bold font-mono tabular-nums leading-none', color)}>{value}</span>
          </div>
        ))}
      </div>

      {/* ── Main 3-column canvas ─────────────────────────────────────────────── */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_220px_220px] xl:grid-cols-[1fr_240px_240px] gap-0 overflow-hidden min-h-0">

        {/* Column 1: Opportunity Surface */}
        <div className="flex flex-col border-r border-surface-4 overflow-hidden min-h-0">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4 bg-surface-0/30 flex-shrink-0">
            <Zap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex-1">Opportunity Surface</p>
            <span className="text-[10px] text-gray-600 font-mono">{filtered.length} picks</span>
          </div>

          <ErrorBoundary label="OpportunitySurface">
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            <AnimatePresence initial={false}>
              {picksLoading && filtered.length === 0 ? (
                <div className="flex flex-col gap-1.5 p-1">
                  {[1, 2, 3, 4].map((i) => (
                    <SkeletonOpportunityRow key={i} />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-4">
                  <Activity className="w-8 h-8 text-gray-700" />
                  <p className="text-xs text-gray-500 leading-snug">
                    {sportFilter !== 'all'
                      ? `No ${sportFilter.toUpperCase()} opportunities found.`
                      : 'No opportunities available. Agents are analyzing upcoming games.'}
                  </p>
                </div>
              ) : (
                filtered.map((rec) => (
                  <motion.div
                    key={rec.id}
                    layout
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.18 }}
                  >
                    <OpportunityCard rec={rec} />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
          </ErrorBoundary>
        </div>

        {/* Column 2: Market Pressure Map */}
        <div className="hidden lg:flex flex-col border-r border-surface-4 overflow-hidden min-h-0">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4 bg-surface-0/30 flex-shrink-0">
            <BarChart2 className="w-3.5 h-3.5 text-warn flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex-1">Market Pressure</p>
          </div>
          <ErrorBoundary label="MarketPressureMap">
          <MarketPressureMap picks={picks} signals={signals} className="flex-1 min-h-0 overflow-y-auto" />
          </ErrorBoundary>
        </div>

        {/* Column 3: Decision Queue */}
        <div className="hidden lg:flex flex-col overflow-hidden min-h-0">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4 bg-surface-0/30 flex-shrink-0">
            <ListChecks className="w-3.5 h-3.5 text-brand flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex-1">Decision Queue</p>
            {betNowCount > 0 && (
              <span className="text-[10px] text-gold font-mono font-bold tabular-nums">{betNowCount}</span>
            )}
          </div>
          <ErrorBoundary label="DecisionQueue">
          <DecisionQueue picks={picks} signals={signals} exposure={exposure} className="flex-1 min-h-0" />
          </ErrorBoundary>
        </div>
      </div>

      {/* ── Market Radar ticker (bottom strip) ──────────────────────────────── */}
      <ErrorBoundary label="MarketRadarTicker">
      <MarketRadarTicker />
      </ErrorBoundary>
    </div>
  );
}
